import {
  SupportThreadDetail,
  SupportReviewRecord,
  SupportSendReceipt,
  SupportExpectation,
} from '../models/supportEmail';
export const ACCESS_DETAILS = '{{ACCESS_DETAILS}}';
export function supportExpectation(thread: SupportThreadDetail): SupportExpectation {
  return {
    reviewVersion: thread.reviewVersion,
    messageRevision: thread.messageRevision,
    processingAttemptId: thread.processingAttemptId,
  };
}
export function currentSupportDraft(thread: SupportThreadDetail): boolean {
  const draft = thread.currentDraft;
  return (
    !!draft &&
    draft.effectiveState !== 'SUPERSEDED' &&
    draft.effectiveState === thread.reviewState &&
    draft.sourceMessageRevision === thread.messageRevision &&
    draft.sourceProcessingAttemptId === thread.processingAttemptId &&
    thread.processingStatus === 'PROCESSED'
  );
}
export function canApproveSupport(thread: SupportThreadDetail): boolean {
  return (
    currentSupportDraft(thread) &&
    thread.reviewState === 'DRAFT_READY' &&
    !!thread.currentDraft.text &&
    !!thread.currentDraft.internalSummary &&
    !thread.currentDraft.internalSummary.requiresInvestigation &&
    ['MISSING_DATA', 'NO_MATCH', 'MATCH'].includes(thread.currentDraft.outcome)
  );
}
export function canSendSupport(thread: SupportThreadDetail): boolean {
  return (
    currentSupportDraft(thread) &&
    thread.reviewState === 'APPROVED' &&
    !!thread.approvalEventId &&
    thread.approvedDraftId === thread.currentDraft.id &&
    !!thread.currentDraft.internalSummary &&
    !thread.currentDraft.internalSummary.requiresInvestigation &&
    ['MISSING_DATA', 'NO_MATCH', 'MATCH'].includes(thread.currentDraft.outcome) &&
    !(thread.sendAttempts || []).some(
      row => ['UNKNOWN', 'SENDING', 'PENDING'].includes(row.state) || row.approvedDraftId === thread.currentDraft.id
    )
  );
}
export function splitSupportDraft(
  draft: SupportReviewRecord
): { before: string; after: string; protected: boolean; valid: boolean } {
  const text = draft.text || '';
  const parts = text.split(ACCESS_DETAILS);
  return {
    before: parts[0],
    after: parts[1] || '',
    protected: draft.outcome === 'MATCH',
    valid: draft.outcome === 'MATCH' ? parts.length === 2 : parts.length === 1,
  };
}
export function composeSupportDraft(before: string, after: string, protectedToken: boolean): string {
  if (/[{}]/.test(before + after))
    throw new Error('Keep system placeholders unchanged; edit only the surrounding prose.');
  return before + (protectedToken ? ACCESS_DETAILS + after : '');
}
export function supportError(error: any): string {
  const code =
    error && error.response && error.response.data && error.response.data.details && error.response.data.details.text;
  if (code === 'SUPPORT_SENDING_DISABLED') return 'Support sending is disabled by backend configuration.';
  if (code === 'SUPPORT_SEND_UNKNOWN_RECONCILE')
    return 'Delivery is uncertain. Do not retry; investigate and reconcile this send.';
  if (code === 'SUPPORT_SEND_REQUIRES_NODE_20')
    return 'Support sending requires a supported Node runtime on the backend.';
  const safeCode = typeof code === 'string' && /^[A-Z0-9_]{1,100}$/.test(code) ? ' (' + code + ')' : '';
  return error && error.response && error.response.status === 409
    ? 'This thread or approval changed, or requires investigation. The view has been refreshed; review it again.' +
        safeCode
    : 'The support request could not be completed. Refresh to check the current state.' + safeCode;
}
export function sendStatusText(receipt: SupportSendReceipt): string {
  if (receipt.state === 'PENDING')
    return 'PENDING — queued for sending by the support delivery worker. Refresh to see the result; do not submit another reply.';
  if (receipt.state === 'UNKNOWN')
    return 'UNKNOWN — delivery is uncertain. Do not resend; investigation and reconciliation are required.';
  if (receipt.state === 'SENT') return 'SENT — accepted by SMTP. Check the recipient mailbox to confirm delivery.';
  if (receipt.state === 'FAILED')
    return 'FAILED — no successful delivery is recorded. Investigate before preparing another reviewed revision.';
  return receipt.state + ' — submission is in progress. Refresh for status; do not submit another reply.';
}
export function supportBadge(value: string): string {
  return value === 'MATCH' || value === 'APPROVED' || value === 'SENT'
    ? 'success'
    : ['AMBIGUOUS', 'MANUAL_REVIEW', 'UNKNOWN', 'NEEDS_REVIEW'].includes(value)
    ? 'warning'
    : ['NO_MATCH', 'FAILED', 'REJECTED'].includes(value)
    ? 'danger'
    : 'secondary';
}
export function supportDate(value?: string): string {
  if (!value || !Number.isFinite(new Date(value).getTime())) return 'Not available';
  return new Date(value).toLocaleString('en-GB');
}
